import '@vaadin/vaadin-overlay';
import { OverlayElement } from '@vaadin/vaadin-overlay';
import { registerStyles, css } from '@vaadin/vaadin-themable-mixin/register-styles.js';
import { Coords } from 'apps/webcomponents/src/grid/gridView/types';

registerStyles(
  'gz-context-menu-overlay',
  css`
    :host {
      left: 0;
      top: calc(var(--spacer) / 2);
      z-index: 9999;
      align-items: stretch;
      width: 240px;
      height: -moz-fit-content;
      height: fit-content;
      margin: 0;
      opacity: 0;
    }

    :host(.fit-content) {
      width: fit-content;
      min-width: 240px;
      max-width: 450px;
    }

    [part='overlay'] {
      border-radius: calc(var(--spacer) / 2);
      background-color: var(--white-layout-303-level-2-elevation);
      box-shadow: 2px 2px 10px 0px var(--chart-050-hover);
      flex-direction: column;
    }

    [part='content'] {
      padding: 0;
    }
  `,
  { moduleId: 'gz-context-menu-overlay-styles' }
);

export enum ContextMenuOverlayEvents {
  OPEN = 'openContextMenu',
  CLOSED = 'closedContextMenu',
}

/**
 * @summary This is a Web Component used for creating overlays for context menus. It is created based on Vaadin overlay component
 * https://cdn.vaadin.com/vaadin-web-components/24.4.0-alpha3/#/elements/vaadin-overlay
 * @tag gz-context-menu-overlay
 * @fires openContextMenu - A custom event dispatched when the overlay element is open. It can be used for adding custom behavior on the overlay content.
 * @fires closedContextMenu - A custom event dispatched after the overlay element is closed
 */
export class ContextMenuOverlayElement extends OverlayElement {
  static get is(): string {
    return 'gz-context-menu-overlay';
  }

  private readonly _boundOnGlobalContextMenu: EventListener;
  private readonly MOUSE_POS_OFFSET = 8;

  constructor() {
    super();
    // ensure that the onGlobalContextMenu method is always called with the correct context
    this._boundOnGlobalContextMenu = this.onGlobalContextMenu.bind(this);
    this.addEventListener('opened-changed', this.onOpenedChanged);
    this.addEventListener('vaadin-overlay-open', this.onOverlayOpen);
  }

  private onGlobalContextMenu(e: CustomEvent): void {
    e.preventDefault();
    this.close();
  }

  private onOpenedChanged(event: CustomEvent): void {
    const opened = event.detail.value;

    if (opened) {
      document.documentElement.addEventListener('contextmenu', this._boundOnGlobalContextMenu, true);
    } else {
      document.documentElement.removeEventListener('contextmenu', this._boundOnGlobalContextMenu, true);
      this.close();
      this.dispatchEvent(new CustomEvent(ContextMenuOverlayEvents.CLOSED));
    }
  }

  private onOverlayOpen(e: CustomEvent): void {
    e.stopImmediatePropagation();
    this.dispatchEvent(new CustomEvent(ContextMenuOverlayEvents.OPEN));
  }

  public openOverlay(coords: Coords): void {
    this.opened = true;
    setTimeout(() => {
      this.alignOverlayToMouse(coords, this);
    }, 0);
  }

  public alignOverlayToMouse(coords: Coords, overlayBox): void {
    const overlayBoxWidth = overlayBox.clientWidth;
    const overlayBoxHeight = overlayBox.clientHeight;

    // Get the mouse coordinates
    let mouseX: number = coords.x + this.MOUSE_POS_OFFSET;
    let mouseY: number = coords.y + this.MOUSE_POS_OFFSET;

    // Calculate the available space in the window
    const windowWidth: number = window.innerWidth;
    const windowHeight: number = window.innerHeight;

    // Check if the div extends beyond the right edge of the window
    if (mouseX + overlayBoxWidth > windowWidth) {
      mouseX = windowWidth - overlayBoxWidth;
    }

    // Check if the div extends beyond the bottom edge of the window
    if (mouseY + overlayBoxHeight > windowHeight) {
      mouseY = mouseY - overlayBoxHeight - this.MOUSE_POS_OFFSET;
    }

    // Set the position of the div
    overlayBox.style.left = mouseX + 'px';
    overlayBox.style.top = mouseY + 'px';

    // Show the div
    overlayBox.style.opacity = '1';
  }
}
window.customElements.define(ContextMenuOverlayElement.is, ContextMenuOverlayElement);
